import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Briefcase, Mail } from 'lucide-react';
import { invitationApi } from '@/lib/api/invitationApi';
import { Invitation } from '@/lib/models/invitation';
import { LoadingState, EmptyState } from '@/components/custom-ui/empty-state';
import { ActionButton } from '@/components/custom-ui/action-button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import { NEXILINK_CARD_WHITE_ELEVATED, NEXILINK_ROW, NEXILINK_SPACING } from '@/lib/nexilink-card-styles';
import { useToast } from '@/hooks/use-toast';

// Initials for the inviter avatar fallback (from their name, else the org).
const initialsOf = (name?: string, org?: string): string => {
    const src = (name || org || '').trim();
    if (!src) return '?';
    const parts = src.split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() || '?';
};

const ReceivedInvitationsPage: React.FC = () => {
    const navigate = useNavigate();
    const { toast } = useToast();
    const [invitations, setInvitations] = useState<Invitation[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [acceptingId, setAcceptingId] = useState<string | null>(null);

    useEffect(() => {
        loadInvitations();
    }, []);

    const loadInvitations = async () => {
        setIsLoading(true);
        try {
            const result = await invitationApi.getReceivedInvitations();
            setInvitations(result.invitations);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAccept = async (inv: Invitation) => {
        // In-app acceptance: the user is logged in and the invitation is addressed to
        // their email, so the backend accepts by id (ownership-checked) — no email
        // token needed.
        setAcceptingId(inv.id);
        try {
            await invitationApi.acceptInvitationById(inv.id);
            toast({
                title: 'Inbjudan accepterad',
                description: 'Du är nu kopplad till organisationen.',
            });
            await loadInvitations();
        } catch (err) {
            toast({
                title: 'Kunde inte acceptera inbjudan',
                description: err instanceof Error ? err.message : 'Försök igen senare.',
                variant: 'destructive',
            });
        } finally {
            setAcceptingId(null);
        }
    };

    if (isLoading) {
        return <LoadingState message="Laddar inbjudningar..." />;
    }

    return (
        <div className={NEXILINK_SPACING}>
            {invitations.length > 0 ? (
                <div className={NEXILINK_CARD_WHITE_ELEVATED}>
                    <div className="px-2 py-2 divide-y divide-gray-50">
                        {invitations.map((inv) => (
                            <div key={inv.id}>
                                <div className={NEXILINK_ROW}>
                                    <Avatar className="w-10 h-10 ring-2 ring-white shadow-sm flex-shrink-0">
                                        <AvatarImage src={resolveMediaUrl(inv.invited_by_picture_url)} alt={inv.invited_by_name || ''} />
                                        <AvatarFallback className="bg-[#1A4D4F] text-[#f5f5f5] text-xs font-semibold">
                                            {initialsOf(inv.invited_by_name, inv.organisation_name)}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-semibold text-gray-900">
                                            {inv.organisation_name
                                                ? `Inbjudan från ${inv.organisation_name}${inv.unit_name ? ` · ${inv.unit_name}` : ''}`
                                                : 'Inbjudan från arbetsgivare'}
                                        </p>
                                        <p className="text-xs text-gray-400">
                                            {inv.invited_by_name && <span>Av {inv.invited_by_name} · </span>}
                                            Mottagen {new Date(inv.invited_at).toLocaleDateString('sv-SE')}
                                            <span> · Giltig till {new Date(inv.expires_at).toLocaleDateString('sv-SE')}</span>
                                        </p>
                                    </div>
                                    <ActionButton
                                        icon={CheckCircle}
                                        onClick={() => handleAccept(inv)}
                                        disabled={acceptingId === inv.id}
                                        className="flex-shrink-0"
                                    >
                                        {acceptingId === inv.id ? 'Accepterar…' : 'Acceptera'}
                                    </ActionButton>
                                </div>
                                {inv.message && (
                                    <div className="mx-4 mb-3 p-3 bg-gray-50 rounded-lg">
                                        <p className="text-sm text-gray-700 italic">"{inv.message}"</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <EmptyState
                    icon={Mail}
                    title="Inga inbjudningar"
                    description="Du har inga väntande inbjudningar just nu. Under tiden kan du söka lediga tjänster."
                    action={{ label: 'Sök jobb', icon: Briefcase, onClick: () => navigate('/dashboard/jobs') }}
                />
            )}
        </div>
    );
};

export default ReceivedInvitationsPage;
